import { JRCButton, JRCButtonProps } from 'components/JRCButton/JRCButton';
import { JRCIcon } from 'components/JRCIcon/JRCIcon';
import { JRCText } from 'components/JRCTypo/JRCTypo';
import * as React from 'react';
import { useIntl } from 'react-intl';
import ReactTooltip from 'react-tooltip';
import styled, { ThemeContext } from 'styled-components';
import { JRCBaseTooltip, TooltipSizes } from './JRCBaseTooltip';

/**
 * Props type for JRCTooltip
 * @member children : enclosed React component (on which the tooltip is set)
 * @member title string, optional: Tooltip title
 * @member description: string : Content of the tooltip
 * @member closeButton: boolean, optional: Add a close button to the tooltip
 * @member button: @see JRCButtonProps, optional: Add a button to the tooltip
 * @member position: optional, "top" | "bottom" | "left" | "right": Chose the placement of the tooltip
 * @member className: optional, overwrite span wrapper style
 */

export interface JRCTooltipProps {
    children: React.ReactNode;
    /** Tooltip title */
    title?: string;
    /** Content of the tooltip */
    description: string;
    /** Add a close button to the tooltip */
    closeButton?: boolean;
    /** Add a button to the tooltip */
    button?: JRCButtonProps;
    /** Chose the placement of the tooltip */
    position?: 'top' | 'bottom' | 'left' | 'right';
    /** overwrite span wrapper style */
    className?: string;
    /** container as block */
    asBlock?: boolean;
    active?: boolean;
    height?: string;
}

const Content = styled.div`
    width: 100%;
`;
const Container = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    flex-direction: row-reverse;
`;
const CloseButton = styled.a`
    cursor: pointer;
    opacity: 50%;
    &:hover {
        opacity: 70%;
    }
`;
const ButtonWrapper = styled.div`
    display: flex;
    justify-content: flex-end;
`;
const Text = styled.span<{ sizeTooltip: TooltipSizes }>`
    margin: 0;
    text-align: ${(props) => (props.sizeTooltip === 'small' ? 'center' : 'initial')};
    word-break: break-word;
`;

export const JRCTooltip = (props: JRCTooltipProps) => {
    const intl = useIntl();

    const sizeTooltip =
        props.closeButton || props.button
            ? 'large'
            : props.title || (props.description && props.description.length > 50)
            ? 'medium'
            : 'small';
    const color = React.useContext(ThemeContext).color;
    const hide = () => {
        ReactTooltip.hide();
    };

    if (props.active === false) {
        return <>{props.children}</>;
    }

    return (
        <>
            <JRCBaseTooltip
                height={props.height}
                asBlock={props.asBlock}
                dataEventOff={props.closeButton && props.button ? 'click' : 'mouseleave'}
                className={props.className}
                color={{
                    background: color.navy,
                    text: color.white,
                }}
                description={props.description}
                sizeTooltip={sizeTooltip}
                position={props.position}
                tooltip={() => (
                    <div>
                        <Container>
                            {sizeTooltip === 'large' && props.closeButton && props.button && (
                                <CloseButton onClick={hide}>
                                    <JRCIcon name="icon-times" size={24} color="white" />
                                </CloseButton>
                            )}
                            <Content>
                                {props.title && <JRCText size="l">{intl.formatMessage({ id: props.title })}</JRCText>}
                                <Text sizeTooltip={sizeTooltip}>{intl.formatMessage({ id: props.description })}</Text>
                            </Content>
                        </Container>
                        <ButtonWrapper>
                            {sizeTooltip === 'large' && props.button && (
                                <JRCButton
                                    {...props.button}
                                    onClick={(e) => {
                                        props.button?.onClick?.(e);
                                        hide();
                                    }}
                                />
                            )}
                        </ButtonWrapper>
                    </div>
                )}>
                {props.children}
            </JRCBaseTooltip>
        </>
    );
};
